/**
 * Dashboard core translations: nav, emailVerification, impersonation, account, loading, empty, sepa, invoice, common
 */

export const nav = {
  legal: {
    ariaLabel: 'Rechtliche Hinweise',
    imprint: 'Impressum',
    privacy: 'Datenschutz',
    terms: 'AGB',
    withdrawal: 'Widerruf',
    cancellation: 'Kündigen'
  },
  logoAlt: 'ITSH - Zur Startseite',
  roles: {
    admin: 'Administrator',
    reseller: 'Reseller',
    customer: 'Kunde'
  },
  viewSwitchLabel: 'Ansicht wechseln',
  viewUser: 'Benutzer',
  viewAdmin: 'Admin',
  viewHintAdmin: 'Du siehst Admin-Funktionen',
  viewHintUser: 'Du siehst die Kunden-Ansicht',
  sections: {
    shop: 'Shop',
    administration: 'Administration',
    customerServices: 'Kundendienste',
    myServices: 'Meine Dienste',
    finance: 'Finanzen',
    account: 'Konto'
  },
  adminGroups: {
    core: 'Kern',
    data: 'Daten',
    finance: 'Finanzen',
    system: 'System'
  },
  items: {
    dashboard: 'Dashboard',
    products: 'Produkte',
    cart: 'Warenkorb',
    users: 'Benutzer',
    invoices: 'Rechnungen',
    featureFlags: 'Feature Flags',
    migration: 'Migration',
    accountSplit: 'Account-Splits',
    compliance: 'Compliance',
    allDomains: 'Alle Domains',
    allHandles: 'Alle Handles',
    featuredTlds: 'Featured TLDs',
    fulfillment: 'Fulfillment',
    webhooks: 'Webhooks',
    email: 'E-Mail',
    domains: 'Domains',
    packages: 'Pakete',
    withdraw: 'Vertrag widerrufen',
    account: 'Account',
    help: 'Hilfe',
    support: 'Support',
    logout: 'Abmelden',
    transferOut: 'Transfer-Out',
    internalTransfers: 'Interne Transfers',
    roles: 'Rollen',
    hosting: 'Webhosting',
    storage: 'Objektspeicher',
    kubernetes: 'Kubernetes',
    registry: 'Container Registry',
    vserver: 'VServer',
    adminStorage: 'Speicher',
    adminKubernetes: 'Kubernetes',
    adminHosting: 'Webhosting',
    adminRegistry: 'Container Registry',
    adminVServer: 'VServer',
    adminMail: 'E-Mail'
  },
  mobileToggle: 'Navigation umschalten',
  version: 'Version'
} as const

export const emailVerification = {
  bannerMessage: 'Deine E-Mail-Adresse wurde noch nicht verifiziert. Einige Funktionen sind erst nach der Verifizierung verfügbar.',
  resend: 'E-Mail erneut senden',
  sent: 'E-Mail gesendet',
  successTitle: 'E-Mail gesendet',
  successMessage: 'Eine Verifizierungs-E-Mail wurde an deine Adresse gesendet.',
  alreadyVerifiedTitle: 'Bereits verifiziert',
  alreadyVerifiedMessage: 'Deine E-Mail-Adresse ist bereits verifiziert.',
  errorTitle: 'Fehler',
  blockingTitle: 'E-Mail-Verifizierung erforderlich',
  blockingMessage: 'Bitte verifiziere deine E-Mail-Adresse, um das Dashboard nutzen zu können. Wir haben dir einen Bestätigungslink gesendet.',
  checkInbox: 'Bitte überprüfe dein Postfach und klicke auf den Bestätigungslink.'
} as const

export const handleVerification = {
  expiredTitle: 'Verifizierung abgelaufen',
  expiredMessage: 'Die Verifizierung von {name} ist abgelaufen. Domain-Registrierungen sind damit nicht möglich.',
  warningTitle: 'Verifizierung läuft ab',
  warningMessage: 'Die Verifizierung von {name} läuft in {days} Tagen ab.',
  infoTitle: 'Verifizierung ausstehend',
  infoMessage: '{name} ist nicht vollständig verifiziert (E-Mail und Telefon erforderlich).',
  action: 'Kontakte prüfen'
} as const

export const impersonation = {
  banner: {
    warning: 'Du agierst als {name}',
    end: 'Impersonation beenden'
  },
  started: {
    title: 'Impersonation gestartet',
    message: 'Du siehst jetzt das Dashboard aus der Perspektive des Benutzers.'
  },
  ended: {
    title: 'Impersonation beendet',
    message: 'Du bist wieder als Admin angemeldet.'
  },
  error: {
    noToken: 'Kein Token vorhanden',
    blocked: 'Diese Aktion ist während einer Impersonation nicht erlaubt.',
    failed: 'Impersonation fehlgeschlagen'
  }
} as const

export const account = {
  title: 'Konto',
  personalData: 'Persönliche Daten',
  paymentMethods: 'Zahlungsmethoden',
  masterData: 'Stammdaten',
  manage: 'Verwalten',
  cancel: 'Kündigen',
  accountSettings: 'Kontoeinstellungen',
  loadingAccountData: 'Lade Kontodaten...',

  tabs: {
    details: 'Stammdaten',
    security: 'Sicherheit',
    payments: 'Zahlungsdaten',
    tokens: 'API Tokens',
    connections: 'Verbundene Anwendungen',
    sshKeys: 'SSH-Schlüssel',
    notifications: 'Benachrichtigungen',
    roles: 'Rollen & Zugriff'
  },

  connections: {
    title: 'Verbundene Anwendungen',
    subtitle: 'KI-Agenten und andere Anwendungen, denen du Zugriff auf dein Konto gegeben hast.',
    loadFailed: 'Die verbundenen Anwendungen konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    empty: {
      title: 'Keine Anwendung verbunden',
      hint: 'Wenn du einen KI-Agenten mit deinem ITSH-Konto verbindest, erscheint er hier. Du siehst, was er darf und was er getan hat, und kannst ihn jederzeit trennen.'
    },
    noVerifiedHost: 'Kein verifizierter Host: die Anwendung hat sich selbst registriert, ihr Name sagt nichts über den Betreiber.',
    status: {
      active: 'Aktiv',
      revoked: 'Getrennt',
      expired: 'Abgelaufen'
    },
    noScopes: 'Keine Berechtigungen.',
    truncated: 'Die neuesten {shown} von {total} Anwendungen.',
    spend: 'Ausgaben',
    spendOf: '{spent} von höchstens {cap}',
    noSpending: 'Darf nichts ausgeben',
    connected: 'Verbunden',
    connectedFrom: 'am {date} von {ip}',
    lastPresented: 'Zuletzt vorgezeigt',
    transferConsent: 'Übermittlung in Drittländer',
    transferConsentGiven: 'Du hast eingewilligt',
    never: 'Noch nie',
    expires: 'Gültig bis',
    noExpiry: 'Unbegrenzt, bis du sie trennst',
    ended: 'Beendet',
    revokedReasons: {
      customer: 'von dir getrennt',
      agent: 'von der Anwendung selbst getrennt',
      admin: 'vom ITSH-Support getrennt',
      account_closed: 'mit der Schließung des Kontos',
      code_replay: 'wegen eines wiederverwendeten Anmeldecodes',
      refresh_reuse: 'wegen eines wiederverwendeten Tokens',
      client_removed: 'weil die Anwendung entfernt wurde',
      expired: 'abgelaufen',
      password_changed: 'weil das Kontopasswort zurückgesetzt oder geändert wurde',
      other: 'beendet'
    },
    tools: {
      account_get: 'Konto anzeigen',
      subscription_list: 'Abos auflisten',
      domain_list: 'Domains auflisten',
      domain_get: 'Domain anzeigen',
      domain_search: 'Verfügbarkeit einer Domain prüfen',
      domain_tld_catalogue: 'Domainendungen auflisten',
      domain_tld_price: 'Preis einer Domainendung anzeigen',
      dns_record_list: 'DNS-Einträge auflisten',
      dns_record_create: 'DNS-Eintrag anlegen',
      dns_record_update: 'DNS-Eintrag ändern',
      dns_record_delete: 'DNS-Eintrag löschen',
      mail_account_list: 'E-Mail-Adressen auflisten',
      mail_account_get: 'E-Mail-Adresse anzeigen',
      mailbox_create: 'Postfach anlegen',
      mail_forward_create: 'Alias anlegen',
      mail_account_update: 'Postfach oder Alias ändern',
      mail_account_delete: 'Postfach oder Alias löschen',
      mailbox_password_link: 'Link zum Setzen eines Postfach-Passworts',
      mail_vacation_get: 'Abwesenheitsnotiz anzeigen',
      mail_vacation_set: 'Abwesenheitsnotiz einstellen',
      mail_catchall_get: 'Catch-All anzeigen',
      mail_catchall_set: 'Catch-All einrichten',
      mail_catchall_disable: 'Catch-All abschalten',
      mail_dns_status: 'Mail-DNS-Einträge prüfen',
      mail_dns_apply: 'Fehlende Mail-DNS-Einträge anlegen',
      mail_dns_replace: 'Mail-DNS-Einträge ersetzen',
      handle_list: 'Domain-Kontakte auflisten',
      handle_create: 'Domain-Kontakt anlegen',
      handle_update: 'Domain-Kontakt ändern',
      handle_verify_request: 'Bestätigungscode senden',
      handle_verify_confirm: 'Bestätigungscode einlösen',
      handle_verify_status: 'Bestätigungsstatus anzeigen',
      grant_get: 'Diese Verbindung anzeigen',
      grant_revoke: 'Sich selbst trennen'
    },
    showActivity: 'Aktivität anzeigen',
    hideActivity: 'Aktivität ausblenden',
    disconnect: {
      action: 'Trennen',
      title: 'Anwendung trennen',
      message: 'Die Anwendung verliert sofort jeden Zugriff auf dein Konto.',
      warning: 'Das lässt sich nicht rückgängig machen. Um sie wieder zu nutzen, verbindest du sie neu. Zugangsdaten, die sie schon erstellt hat, funktionieren weiter, bis du sie einzeln zurückziehst.',
      confirm: 'Trennen',
      working: 'Wird getrennt',
      success: 'Anwendung getrennt',
      failed: 'Die Anwendung konnte nicht getrennt werden'
    },
    activity: {
      loadFailed: 'Die Aktivität konnte nicht geladen werden.',
      callsTitle: 'Was die Anwendung getan hat',
      callsHint: 'Festgehalten, bevor es ausgeführt wird: jede Änderung und alles, was über das Lesen hinausgeht, etwa das Abrufen von Zugangsdaten. Reines Lesen steht hier nicht.',
      callsEmpty: 'Diese Anwendung hat noch nichts geändert. Wann sie zuletzt zugegriffen hat, steht oben unter „Zuletzt vorgezeigt“.',
      directRequest: 'Direkte Anfrage',
      outcomes: {
        attempted: 'Läuft',
        ok: 'Erledigt',
        client_error: 'Abgelehnt',
        server_error: 'Fehlgeschlagen',
        denied: 'Nicht erlaubt',
        unknown: 'Ergebnis unbekannt'
      },
      unknownHint: 'Die Anfrage wurde abgebrochen, bevor eine Antwort kam. Ob sie etwas geändert hat, wissen wir nicht. Sieh am besten direkt nach.',
      previous: 'Vorher:',
      argsTruncated: 'Einige Werte wurden gekürzt oder weggelassen, damit der Eintrag passt.',
      externalTargets: 'Weiterleitung an Adressen außerhalb deiner Domains angefordert:',
      oversize: '{bytes} Bytes, zu lang zum Speichern',
      digest: 'SHA-256-Prüfsumme:',
      yes: 'ja',
      no: 'nein',
      none: 'keine',
      args: {
        domain: 'Domain',
        address: 'Adresse',
        id: 'ID',
        mailbox_id: 'Postfach-ID',
        channel: 'Kanal',
        type: 'Typ',
        name: 'Name',
        content: 'Inhalt',
        ttl: 'TTL',
        priority: 'Priorität',
        country: 'Land',
        active: 'Eingeschaltet',
        targets: 'Leitet weiter an',
        enabled: 'Abwesenheitsnotiz an',
        from_date: 'Von',
        to_date: 'Bis',
        subject: 'Betreff',
        message: 'Text',
        deleted_address: 'Gelöschte Adresse',
        deleted_type: 'Gelöschte Art'
      },
      values: {
        channel: {
          email: 'E-Mail',
          phone: 'Telefon'
        },
        deleted_type: {
          mailbox: 'Postfach',
          alias: 'Alias'
        }
      },
      refusedTitle: 'Abgelehnte Anfragen',
      refusedHint: 'Anfragen, die wir mit dem Zugriff dieser Anwendung abgelehnt haben, 30 Tage lang.',
      refusedEmpty: 'Keine abgelehnten Anfragen.',
      noRoute: '(keine bekannte Adresse)',
      truncated: 'Die neuesten {shown} von {total}.',
      needed: 'Braucht: {scopes}',
      reasons: {
        scope: 'Dafür hat die Anwendung keine Berechtigung',
        session_only: 'Geht nur im Control Panel selbst',
        unmapped: 'Keine Adresse der Schnittstelle',
        admin: 'Verwaltungsbereich von ITSH',
        audience: 'Zugriff für eine andere Schnittstelle ausgestellt',
        agent_access: 'Agenten sind für dein Konto noch nicht freigeschaltet',
        other: 'Abgelehnt'
      }
    }
  },

  notifications: {
    title: 'Benachrichtigungen',
    subtitle: 'Lege fest, worüber wir dich per E-Mail informieren.',
    saved: 'Einstellungen gespeichert.',
    vserverDown: {
      label: 'E-Mail, wenn mein Server unerwartet ausfällt',
      hint: 'Wir benachrichtigen dich, sobald dein Server unerwartet offline geht.'
    },
    vserverDiskUsage: {
      label: 'E-Mail, wenn die Festplatte meines Servers fast voll ist',
      hint: 'Du erhältst eine Warnung, bevor der Speicherplatz deines Servers ausgeht.'
    }
  },

  roles: {
    title: 'Rollen & Zugriff',
    subtitle: 'Deine zugewiesenen Rollen und delegierter Domain-Zugriff',
    loading: 'Rollen werden geladen...',
    yourRoles: 'Deine Rollen',
    noRoles: 'Keine Rollen zugewiesen',
    noRolesDescription: 'Du hast derzeit keine speziellen Rollen.',
    delegatedAccess: 'Delegierter Zugriff',
    noDelegations: 'Kein delegierter Zugriff',
    noDelegationsDescription: 'Andere Benutzer haben dir keinen Zugriff auf ihre Domains gewährt.',
    delegatedBy: 'Delegiert von',
    permissions: 'Berechtigungen',
    grantedAt: 'Gewährt am',
    expiresAt: 'Läuft ab am',
    never: 'Nie',
    goToDomain: 'Zur Domain'
  },

  avv: {
    navTitle: 'AVV',
    title: 'Auftragsverarbeitungsvertrag',
    subtitle: 'Datenschutzvereinbarung gemäß Art. 28 DSGVO',
    loading: 'AVV-Status wird geladen...',
    previewTitle: 'Bitte lies und akzeptiere den Auftragsverarbeitungsvertrag',
    acceptButton: 'AVV akzeptieren',
    accepting: 'Wird akzeptiert...',
    acceptedTitle: 'AVV akzeptiert',
    acceptedInfo: 'Akzeptiert am {date}',
    downloadButton: 'PDF herunterladen',
    newVersionAvailable: 'Eine neue Version des AVV ist verfügbar',
    emailNotVerified: 'Bitte bestätige zuerst deine E-Mail-Adresse',
    error: {
      loadFailed: 'AVV-Status konnte nicht geladen werden',
      acceptFailed: 'AVV konnte nicht akzeptiert werden',
      pdfFailed: 'Das PDF konnte nicht geladen werden',
      retry: 'Erneut versuchen'
    }
  },

  apiTokens: {
    title: 'API Tokens',
    subtitle: 'Verwalte deine API Tokens für externe Integrationen',
    createToken: 'Token erstellen',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    never: 'Nie',
    copied: 'Kopiert',
    copy: 'Kopieren',

    columns: {
      name: 'Name',
      created: 'Erstellt',
      lastUsed: 'Zuletzt verwendet',
      status: 'Status',
      actions: 'Aktionen'
    },

    empty: {
      title: 'Keine API Tokens',
      description: 'Erstelle einen API Token, um externe Dienste mit deinem Account zu verbinden.'
    },

    create: {
      title: 'API Token erstellen',
      successTitle: 'Token erstellt',
      name: 'Name',
      namePlaceholder: 'z.B. acme.sh production',
      nameHint: 'Wähle einen aussagekräftigen Namen, um den Token später identifizieren zu können.',
      warning: 'Speichere diesen Token jetzt - er wird nicht erneut angezeigt.'
    },

    delete: {
      title: 'Token löschen',
      message: 'Bist du sicher, dass du diesen Token löschen möchtest?',
      warning: 'Alle Anwendungen, die diesen Token verwenden, werden den Zugriff verlieren.',
      confirm: 'Löschen'
    },

    notifications: {
      createSuccess: 'Token erstellt',
      deleteSuccess: 'Token gelöscht',
      maxTokensReached: 'Maximale Anzahl an Tokens erreicht'
    }
  },

  sshKeys: {
    title: 'SSH-Schlüssel',
    subtitle: 'Verwalte deine öffentlichen SSH-Schlüssel für den Zugriff auf deine Server',
    createKey: 'Schlüssel hinzufügen',
    active: 'Aktiv',
    inactive: 'Inaktiv',

    columns: {
      name: 'Name',
      fingerprint: 'Fingerprint',
      created: 'Erstellt',
      status: 'Status',
      actions: 'Aktionen'
    },

    empty: {
      title: 'Keine SSH-Schlüssel',
      description: 'Füge einen öffentlichen SSH-Schlüssel hinzu, um dich bei deinen Servern anzumelden.'
    },

    create: {
      title: 'SSH-Schlüssel hinzufügen',
      name: 'Name',
      namePlaceholder: 'z.B. mein-laptop',
      nameHint: 'Wähle einen aussagekräftigen Namen, um den Schlüssel später identifizieren zu können.',
      publicKey: 'Öffentlicher Schlüssel',
      publicKeyPlaceholder: 'ssh-ed25519 AAAA...',
      publicKeyHint: 'Füge deinen vollständigen öffentlichen SSH-Schlüssel ein (beginnt z.B. mit ssh-ed25519 oder ssh-rsa).',
      invalidKey: 'Das sieht nicht nach einem gültigen öffentlichen SSH-Schlüssel aus.'
    },

    delete: {
      title: 'Schlüssel löschen',
      message: 'Bist du sicher, dass du diesen SSH-Schlüssel löschen möchtest?',
      warning: 'Zugriffe, die auf diesem Schlüssel basieren, gehen verloren.',
      confirm: 'Löschen'
    },

    notifications: {
      createSuccess: 'SSH-Schlüssel hinzugefügt',
      deleteSuccess: 'SSH-Schlüssel gelöscht'
    }
  },

  stammdaten: {
    title: 'Persönliche Daten',
    description: 'Verwalte deine Kontaktdaten und Adressinformationen',
    edit: 'Bearbeiten',
    saveFailed: 'Stammdaten konnten nicht gespeichert werden!',
    saveSuccess: 'Stammdaten wurden erfolgreich gespeichert!',

    name: 'Name',
    organization: 'Organisation',
    organizationName: 'Organisationsname',
    firstName: 'Vorname',
    lastName: 'Nachname',
    address: 'Adresse',
    street: 'Straße',
    streetAndNumber: 'Straße und Hausnummer',
    addressSupplement: 'Adresszusatz',
    optional: 'Optional',
    postcode: 'Postleitzahl',
    postalCode: 'PLZ',
    city: 'Stadt',
    state: 'Bundesland',
    country: 'Land',
    contact: 'Kontakt',
    phone: 'Telefon',
    phoneNumber: 'Telefonnummer',
    email: 'E-Mail',
    emailAddress: 'E-Mail-Adresse',
    emailCannotChange: 'E-Mail kann nicht geändert werden',
    vatId: 'Umsatzsteuer-ID',
    vatIdHint: 'Wir prüfen die Nummer im VIES-System der EU-Kommission und danach alle 30 Tage erneut; bei einer Nummer aus einem anderen EU-Land zusammen mit Firmenname und Rechnungsanschrift, bei einer deutschen nur die Nummer. Ändert sich dadurch deine Preisbasis, bekommst du eine E-Mail. Details in der Datenschutzerklärung.',
    language: 'Sprache',

    cancel: 'Abbrechen',
    save: 'Speichern',
    saving: 'Speichert...',

    emailChange: {
      title: 'E-Mail-Adresse ändern',
      currentEmail: 'Aktuelle E-Mail',
      newEmail: 'Neue E-Mail-Adresse',
      newEmailPlaceholder: "neue{'@'}email.de",
      requestChange: 'Änderung anfordern',
      verifyTitle: 'E-Mail bestätigen',
      verifyDescription: 'Wir haben einen Bestätigungscode an {email} gesendet.',
      otpPlaceholder: 'Bestätigungscode eingeben',
      currentCodeEmail: 'Code an deine aktuelle Adresse {email}',
      currentCodeTotp: 'Code aus deiner Authenticator-App',
      currentCodeHint: 'Damit bestätigst du, dass die Änderung von dir kommt.',
      verify: 'Bestätigen',
      cancel: 'Abbrechen',
      logoutWarning: 'Du wirst ausgeloggt',
      logoutWarningText:
        'Nach der Bestätigung wirst du automatisch abgemeldet und musst dich mit deiner neuen E-Mail-Adresse erneut anmelden.',
      success: 'E-Mail-Adresse geändert',
      successMessage: 'Bitte melde dich mit deiner neuen E-Mail-Adresse an.',
      errors: {
        invalidEmail: 'Bitte gib eine gültige E-Mail-Adresse ein',
        sameEmail: 'Die neue E-Mail-Adresse muss sich von der aktuellen unterscheiden',
        emailInUse: 'Diese E-Mail-Adresse wird bereits verwendet',
        emailNotVerified: 'Deine aktuelle E-Mail-Adresse muss zuerst bestätigt sein',
        invalidOtp: 'Ungültiger Bestätigungscode',
        noPendingChange: 'Keine ausstehende E-Mail-Änderung gefunden',
        currentCodeInvalid: 'Der Code an deine aktuelle Adresse stimmt nicht',
        currentTotpInvalid: 'Der Code aus deiner Authenticator-App stimmt nicht',
        changeExpired: 'Diese Änderung ist abgelaufen oder hatte zu viele falsche Codes. Bitte fordere sie neu an.',
        mailUnavailable: 'Wir konnten die Bestätigungscodes nicht senden. Bitte versuche es später erneut.',
        tooManyRequests: 'Zu viele Anfragen. Bitte warte einen Moment.',
        generic: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.'
      }
    }
  },

  payments: {
    title: 'Zahlungsmethoden',
    description: 'Verwalte deine gespeicherten Zahlungsmethoden',
    loading: 'Lade Zahlungsmethoden...',
    empty: 'Keine Zahlungsmethoden',
    emptyHint: 'Füge eine Zahlungsmethode hinzu, um deine Rechnungen zu bezahlen.',
    addMethod: 'Zahlungsmethode hinzufügen',
    addingMethod: 'Wird hinzugefügt...',
    add: 'Hinzufügen',

    active: 'Aktiv',
    revoked: 'Widerrufen',
    since: 'Seit',

    iban: 'IBAN',
    ibanLabel: 'IBAN',
    cardNumber: 'Kartennummer',
    cardHolder: 'Karteninhaber',
    ibanPlaceholder: 'DE89 3704 0044 0532 0130 00',
    bankDataLoadFailed: 'Bankdaten konnten nicht geladen werden.',
    manualKey: 'Manueller Schlüssel:',
    accountHolder: 'Kontoinhaber',
    bankName: 'Bank',
    creditor: 'Zahlungsempfänger',
    paypalAccount: 'PayPal-Konto',

    sepaMandate: 'SEPA-Lastschriftmandat',
    mandateIntro: 'Hiermit erteile ich,',
    mandateRecipient: 'dem Zahlungsempfänger IT-Service Harting (Gläubiger-Identifikationsnummer: NL08ZZZ502057730000) die Erlaubnis, Zahlungen von meinem Konto mit der IBAN',
    mandateBank: 'bei dem Kreditinstitut',
    mandateDirectDebit: 'mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die vom Zahlungsempfänger auf mein Konto gezogenen Lastschriften einzulösen.',
    mandateConsent: 'Ich erteile die SEPA-Lastschriftermächtigung und weise mein Kreditinstitut an, die Lastschriften einzulösen.',
    mandateInfo: 'Die Frist für die Vorabankündigung wird auf 1 Tag reduziert. Du bist berechtigt, eine Erstattung innerhalb von 8 Wochen ab Belastungsdatum zu verlangen.',

    revokeTitle: 'Zahlungsmethode widerrufen?',
    revokeDescription: 'Möchtest du diese Zahlungsmethode wirklich widerrufen? Du kannst nach dem Widerruf keine Zahlungen mehr mit diesem Konto durchführen.',
    revokeConfirm: 'Widerrufen',
    revokeMethod: 'Zahlungsmethode widerrufen',
    cannotRevokeLastMethod: 'Du kannst die letzte aktive Zahlungsmethode nicht widerrufen.',
    cannotDeleteLast: 'Diese Zahlungsmethode kann nicht entfernt werden, solange aktive Abonnements bestehen.',

    loadingStripe: 'Lade Zahlungsformular...',
    validationError: 'Bitte überprüfe deine Eingaben.',
    setupFailed: 'Zahlungsmethode konnte nicht eingerichtet werden.',
    unknownError: 'Ein unbekannter Fehler ist aufgetreten.',
    addedTitle: 'Zahlungsmethode hinzugefügt',
    addedMessage: 'Deine Zahlungsmethode wurde erfolgreich hinzugefügt.',
    securityNote: 'Deine Daten werden verschlüsselt übertragen.',

    defaultMethod: 'Standard',
    setDefault: 'Als Standard festlegen',
    setDefaultSuccess: 'Zahlungsmethode als Standard festgelegt.',

    cancel: 'Abbrechen',
    phoneRequiredTitle: 'Telefon-Verifizierung erforderlich',
    phoneRequired: 'Bitte bestätige deine Telefonnummer unter Kontakte, um Zahlungsmethoden hinzuzufügen.'
  },

  security: {
    title: 'Sicherheitseinstellungen',
    description: 'Verwalte deine Authentifizierung und Zugangsdaten',

    twoFactor: '2-Faktor Authentifizierung',
    twoFactorDescription: 'Zusätzliche Sicherheit für dein Konto',
    twoFactorActive: 'Dein Konto ist durch 2-Faktor Authentifizierung geschützt.',
    twoFactorActivate: 'Aktiviere die 2-Faktor Authentifizierung für zusätzlichen Schutz. Du benötigst eine Authenticator-App wie Google Authenticator oder Authy.',
    setupOtp: 'OTP Einrichten',
    preparing: 'Wird vorbereitet...',
    activated: 'Aktiviert',
    notActivated: 'Nicht aktiviert',
    setupSuccess: '2-Faktor Authentifizierung wurde erfolgreich eingerichtet!',

    scanQrCode: 'Scanne diesen QR-Code mit deiner Authenticator-App',
    qrCodeAlt: 'QR-Code für 2FA',

    verificationCode: 'Verifizierungscode',
    verificationPlaceholder: '6-stelliger Code',
    verificationHint: 'Gib den 6-stelligen Code aus deiner Authenticator-App ein',
    verify: 'Bestätigen',
    verifying: 'Wird überprüft...',
    cancel: 'Abbrechen',
    emailCode: 'Code aus deiner E-Mail',
    emailCodeHint: 'Wir haben dir einen Code per E-Mail geschickt. Er ist 10 Minuten gültig.',
    totpAlreadyEnabled: 'Für dein Konto ist die Zwei-Faktor-Authentifizierung bereits eingerichtet.',
    setupExpired: 'Diese Einrichtung ist abgelaufen oder hatte zu viele falsche Codes. Bitte starte neu.',
    emailCodeInvalid: 'Der Code aus der E-Mail stimmt nicht.',
    totpInvalid: 'Der Code aus deiner Authenticator-App stimmt nicht.',
    mailUnavailable: 'Wir konnten den E-Mail-Code nicht senden. Bitte versuche es später erneut.',
    tooManyAttempts: 'Zu viele Versuche. Bitte warte ein paar Minuten und versuche es erneut.',

    passwordChange: 'Passwort ändern',
    passwordChangeDescription: 'Aktualisiere dein Kontopasswort',
    currentPassword: 'Aktuelles Passwort',
    currentPasswordPlaceholder: 'Dein aktuelles Passwort',
    newPassword: 'Neues Passwort',
    newPasswordPlaceholder: 'Neues Passwort',
    confirmPassword: 'Passwort bestätigen',
    confirmPasswordPlaceholder: 'Passwort wiederholen',
    passwordsNoMatch: 'Passwörter stimmen nicht überein',
    confirmationCode: 'Bestätigungscode',
    confirmationCodePlaceholder: '6-stelliger Code',
    confirmationCodeHint: 'Gib den Code aus deiner Authenticator-App ein',
    passwordChanged: 'Passwort wurde erfolgreich geändert!',
    passwordChangeFailed: 'Passwort konnte nicht geändert werden',
    requestOtp: 'OTP anfordern',
    changePassword: 'Passwort ändern',
    processing: 'Wird verarbeitet...',

    sessions: {
      title: 'Aktive Sitzungen',
      description: 'Deine aktuell aktiven Anmeldesitzungen',
      current: 'Aktuelle Sitzung',
      revoke: 'Abmelden',
      revokeConfirm: 'Diese Sitzung beenden? Das Gerät wird abgemeldet.',
      revokeSuccess: 'Sitzung beendet',
      empty: 'Keine aktiven Sitzungen',
      unknownLocation: 'Unbekannter Standort',
      unknownDevice: 'Unbekanntes Gerät'
    }
  }
} as const

export const loading = {
  paymentMethods: 'Lade Zahlungsmethoden...'
} as const

export const empty = {
  paymentMethods: 'Keine Zahlungsmethoden',
  paymentMethodsHint: 'Füge eine Zahlungsmethode hinzu, um deine Rechnungen zu bezahlen.'
} as const

export const sepa = {
  addMethod: 'Zahlungsmethode hinzufügen',
  mandate: 'Hiermit erteile ich...'
} as const

export const invoice = {
  title: 'Rechnungen',
  subtitle: 'Deine Rechnungen und Bestellungen',
  loading: 'Rechnungen werden geladen...',
  allInvoices: 'Alle Rechnungen',
  allOrders: 'Alle Bestellungen',
  ordersHint: 'Hier stehen deine Käufe im Shop. Verlängerungen und monatliche Abrechnungen findest du unter Rechnungen.',
  entries: '{count} Einträge',
  due: 'Fällig',
  pdfFilename: 'ITSH-Rechnung',
  viewReceipt: 'Beleg',
  tabs: {
    invoices: 'Rechnungen',
    orders: 'Bestellungen'
  },
  purpose: 'Verwendung',
  period: 'Zeitraum: {start} bis {end}',
  remaining: 'Offen: {amount}',
  downloadPdf: 'PDF',
  viewOnline: 'Online',
  linkError: 'Die Rechnung lässt sich gerade nicht öffnen. Versuch es gleich noch einmal.',
  linkHolderOnly: 'Offene Rechnungen kann nur der Kontoinhaber öffnen, nicht während einer Impersonation.',
  invoiceStatus: {
    paid: 'Bezahlt',
    open: 'Offen',
    void: 'Storniert',
    uncollectible: 'Nicht bezahlt'
  },
  stats: {
    invoices: 'Rechnungen',
    paid: 'Bezahlt',
    open: 'Offen',
    total: 'Gesamt'
  },
  status: {
    paid: 'Bezahlt',
    open: 'Offen',
    refunded: 'Erstattet',
    processing: 'In Bearbeitung'
  },
  empty: {
    title: 'Keine Rechnungen vorhanden',
    description: 'Sobald du eine Bestellung tätigst, werden deine Rechnungen hier angezeigt.'
  },
  ordersEmpty: {
    title: 'Keine Bestellungen vorhanden',
    description: 'Sobald du etwas bestellst, erscheint es hier.'
  },
  estimate: {
    title: 'Voraussichtliche Kosten',
    subtitle: 'Geschätzte Kosten für den aktuellen Abrechnungszeitraum',
    period: 'Zeitraum',
    category: {
      hosting: 'Hosting',
      domain: 'Domains',
      k8s: 'Kubernetes',
      s3: 'Objektspeicher',
      registry: 'Container Registry',
      vserver: 'VServer'
    },
    estimated: 'geschätzt',
    net: 'Netto',
    vat: 'USt. 19 %',
    reverseChargeNote: 'Keine USt.: Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)',
    notTaxableNote: 'Keine USt.: in Deutschland nicht steuerbar',
    total: 'Gesamt',
    empty: 'Keine aktiven Abonnements in diesem Zeitraum',
    loading: 'Kostenvoranschlag wird berechnet...',
    monthlyEstimate: 'Voraussichtliche Kosten'
  }
} as const

export const dashboardCommon = {
  loading: 'Laden...',
  noData: 'Keine Daten',
  noEntries: 'Es sind keine Einträge vorhanden.',
  confirm: 'Bestätigen',
  cancel: 'Abbrechen'
} as const
